import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  CircularProgress,
  Alert,
  Button,
  Stack,
  alpha,
  useTheme,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { 
  Refresh as ResetIcon,
  Fullscreen as MaximizeIcon,
  FullscreenExit as MinimizeIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { snowflakeApi } from '../../utils/api';

interface PromptEditorV2Props {
  module: 'module1' | 'module3' | 'module4' | 'module1_v2' | 'module3_v2' | 'module4_v2';
  onPromptChange: (prompt: string | null) => void;
  title?: string;
  description?: string;
}

// Default prompts as fallback
const DEFAULT_PROMPTS: Record<string, string> = {
  module1: `You are an expert in keyword analysis and topic modeling. Your task is to analyze a list of keywords and identify:
1. Main topics that the keywords belong to
2. Subtopic categories within each main topic

For each topic and subtopic, provide:
- A clear, descriptive name
- A sampling of keywords that belong to that category

You MUST format your response as a valid JSON object with the following structure:
{
    "topics": [
        {
            "name": "Topic Name",
            "subtopics": [
                {
                    "name": "Subtopic Name",
                    "keywords": ["keyword1", "keyword2", "keyword3"]
                }
            ]
        }
    ]
}

IMPORTANT: Your response must be ONLY the JSON object, with no additional text, markdown formatting, or code blocks.
The JSON must be properly formatted with:
- All property names in double quotes
- All string values in double quotes
- No trailing commas
- No comments
- No line breaks within arrays or string values

Ensure that:
1. Topics and subtopics are based on subject matter themes
2. Each subtopic contains a representative sample of keywords (aim for 5-10 keywords per subtopic)
3. Include a "Brand" topic with appropriate subtopics based on the business context
4. Focus on creating clear, meaningful topic hierarchies that will help organize the keywords effectively`,
  module3: `You are an AI assistant specialized in semantic classification of keywords. Your task is to classify keywords into the provided topic framework categories based on their semantic meaning and relevance.

Given the topic framework and list of keywords, classify each keyword into the most appropriate category. Consider:
1. The semantic meaning of each keyword
2. The context provided by the topic framework
3. Industry-specific terminology and jargon

Format your response according to the specified structure.`,
  module4: `You are an AI assistant specialized in batch keyword classification. Your task is to classify keywords into the provided topic hierarchy and intent categories based on their semantic meaning and relevance.

Given the merged configuration with topics, subtopics, and intent categories, classify each keyword into:
1. The most appropriate topic and subtopic
2. The most relevant intent category

Consider:
- The semantic meaning of each keyword
- The context provided by the topic framework
- Industry-specific terminology and jargon
- User search intent patterns

Format your response according to the specified JSON structure.`
};

// V2 modules use the same fallback prompts as their base versions
DEFAULT_PROMPTS.module1_v2 = DEFAULT_PROMPTS.module1;
DEFAULT_PROMPTS.module3_v2 = DEFAULT_PROMPTS.module3;
DEFAULT_PROMPTS.module4_v2 = DEFAULT_PROMPTS.module4;

const DEFAULT_TITLE = 'Analysis Prompt Configuration';
const DEFAULT_DESCRIPTION = 'Customize the AI analysis prompt to adjust how the AI processes your keywords.';

const PromptEditorV2: React.FC<PromptEditorV2Props> = ({ module, onPromptChange, title = DEFAULT_TITLE, description = DEFAULT_DESCRIPTION }) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [defaultPrompt, setDefaultPrompt] = useState<string>('');
  const [currentPrompt, setCurrentPrompt] = useState<string>('');
  const [isModified, setIsModified] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);

  // Fetch default prompt
  useEffect(() => {
    const fetchDefaultPrompt = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await snowflakeApi.get(`/api/v1/prompts/defaults/${module}`);
        const data = response.data;
        const prompt = data.prompt || '';
        setDefaultPrompt(prompt);
        setCurrentPrompt(prompt);
        // Send the default prompt to parent
        onPromptChange(prompt);
      } catch (err) {
        console.error('Error fetching default prompt:', err);
        // Use fallback prompt
        const fallbackPrompt = DEFAULT_PROMPTS[module];
        setDefaultPrompt(fallbackPrompt);
        setCurrentPrompt(fallbackPrompt);
        // Send the fallback prompt to parent
        onPromptChange(fallbackPrompt);
        setError('Using fallback prompt. API endpoint not available.');
      } finally {
        setLoading(false);
      }
    };

    fetchDefaultPrompt();
  }, [module]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePromptChange = (newPrompt: string) => {
    setCurrentPrompt(newPrompt);
    const modified = newPrompt !== defaultPrompt;
    setIsModified(modified);
    // Always send the prompt (even if not modified from default)
    onPromptChange(newPrompt);
  };

  const handleReset = () => {
    setCurrentPrompt(defaultPrompt);
    setIsModified(false);
    // Send the default prompt when resetting
    onPromptChange(defaultPrompt);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2 }}>
        <CircularProgress size={20} />
        <Typography variant="body2" color="text.secondary">
          Loading default prompt...
        </Typography>
      </Box>
    );
  }

  return (
    <>
      {/* Normal View */}
      <Box
        sx={{
          p: 2,
          borderRadius: 1,
          backgroundColor: alpha(theme.palette.primary.main, 0.03),
          border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
        }}
      >
        {/* Header with instruction and maximize button */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
          <Typography 
            variant="body2" 
            sx={{ 
              color: 'primary.main',
              fontWeight: 500,
            }}
          >
            {description}
            {isModified && (
              <Box component="span" sx={{ ml: 1, color: 'warning.main', fontWeight: 600 }}>
                (Modified)
              </Box>
            )}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {isModified && (
              <Button
                size="small"
                startIcon={<ResetIcon />}
                onClick={handleReset}
                variant="outlined"
                color="primary"
              >
                Reset
              </Button>
            )}
            <IconButton
              onClick={() => setIsMaximized(true)}
              size="small"
              title="Maximize editor"
              sx={{ 
                color: 'primary.main',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                }
              }}
            >
              <MaximizeIcon />
            </IconButton>
          </Box>
        </Stack>

        {/* Error alert */}
        {error && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Prompt text field - normal size */}
        <TextField
          fullWidth
          multiline
          rows={12}
          value={currentPrompt}
          onChange={(e) => handlePromptChange(e.target.value)}
          placeholder="Enter your custom prompt here..."
          variant="outlined"
          sx={{
            '& .MuiOutlinedInput-root': {
              fontFamily: 'Consolas, Monaco, "Andale Mono", "Ubuntu Mono", monospace',
              fontSize: '13px',
              lineHeight: 1.6,
              backgroundColor: theme.palette.mode === 'dark' 
                ? alpha(theme.palette.background.paper, 0.5)
                : '#ffffff',
              '& fieldset': {
                borderColor: alpha(theme.palette.primary.main, 0.3),
                borderWidth: '2px',
              },
              '&:hover fieldset': {
                borderColor: alpha(theme.palette.primary.main, 0.5),
              },
              '&.Mui-focused fieldset': {
                borderColor: theme.palette.primary.main,
                borderWidth: '2px',
              },
            },
            '& .MuiInputBase-input': {
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: alpha(theme.palette.divider, 0.1),
              },
              '&::-webkit-scrollbar-thumb': {
                background: alpha(theme.palette.primary.main, 0.3),
                borderRadius: '4px',
                '&:hover': {
                  background: alpha(theme.palette.primary.main, 0.5),
                },
              },
            },
          }}
        />

        {/* Character count */}
        <Typography 
          variant="caption" 
          sx={{ 
            mt: 1, 
            display: 'block',
            color: 'primary.main',
            fontWeight: 500,
          }}
        >
          {currentPrompt.length} characters
        </Typography>
      </Box>

      {/* Maximized View - Full Screen Dialog */}
      <Dialog
        open={isMaximized}
        onClose={() => setIsMaximized(false)}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            width: '95vw',
            height: '95vh',
            maxWidth: '95vw',
            maxHeight: '95vh',
            m: 2
          }
        }}
      >
        <DialogTitle sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600 }}>
                {title}
                {isModified && (
                  <Box component="span" sx={{ ml: 2, color: 'warning.main', fontWeight: 600, fontSize: '0.9rem' }}>
                    (Modified)
                  </Box>
                )}
              </Typography>
              <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 500, mt: 0.5 }}>
                {description}
              </Typography>
            </Box>
            <IconButton
              onClick={() => setIsMaximized(false)}
              size="small"
              title="Minimize editor"
              sx={{ 
                color: 'primary.main',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                }
              }}
            >
              <MinimizeIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Error alert */}
          {error && (
            <Alert severity="warning">
              {error}
            </Alert>
          )}

          {/* Prompt text field - maximized */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <TextField
              fullWidth
              multiline
              value={currentPrompt}
              onChange={(e) => handlePromptChange(e.target.value)}
              placeholder="Enter your custom prompt here..."
              variant="outlined"
              sx={{
                flex: 1,
                '& .MuiOutlinedInput-root': {
                  height: '100%',
                  alignItems: 'flex-start',
                  fontFamily: 'Consolas, Monaco, "Andale Mono", "Ubuntu Mono", monospace',
                  fontSize: '14px',
                  lineHeight: 1.6,
                  backgroundColor: theme.palette.mode === 'dark' 
                    ? alpha(theme.palette.background.paper, 0.5)
                    : '#ffffff',
                  '& fieldset': {
                    borderColor: alpha(theme.palette.primary.main, 0.3),
                    borderWidth: '2px',
                  },
                  '&:hover fieldset': {
                    borderColor: alpha(theme.palette.primary.main, 0.5),
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: theme.palette.primary.main,
                    borderWidth: '2px',
                  },
                },
                '& .MuiInputBase-input': {
                  height: '100% !important',
                  overflow: 'auto !important',
                  '&::-webkit-scrollbar': {
                    width: '8px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: alpha(theme.palette.divider, 0.1),
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: alpha(theme.palette.primary.main, 0.3),
                    borderRadius: '4px',
                    '&:hover': {
                      background: alpha(theme.palette.primary.main, 0.5),
                    },
                  },
                },
              }}
            />
          </Box>

          {/* Character count */}
          <Typography 
            variant="caption" 
            sx={{ 
              color: 'primary.main',
              fontWeight: 500,
            }}
          >
            {currentPrompt.length} characters
          </Typography>
        </DialogContent>
        {isModified && (
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button
              startIcon={<ResetIcon />}
              onClick={handleReset}
              variant="outlined"
              color="primary"
            >
              Reset to Default
            </Button>
          </DialogActions>
        )}
      </Dialog>
    </>
  );
};

export default PromptEditorV2;

